package com.mgacreative.hotel.feature.reservation.ui

import androidx.compose.foundation.background
import androidx.compose.foundation.border
import androidx.compose.foundation.clickable
import androidx.compose.foundation.horizontalScroll
import androidx.compose.foundation.verticalScroll
import androidx.compose.foundation.layout.*
import androidx.compose.foundation.lazy.LazyColumn
import androidx.compose.foundation.lazy.items
import androidx.compose.foundation.rememberScrollState
import androidx.compose.foundation.shape.CircleShape
import androidx.compose.foundation.shape.RoundedCornerShape
import androidx.compose.material.icons.Icons
import androidx.compose.material.icons.filled.*
import androidx.compose.material3.*
import androidx.compose.runtime.*
import androidx.compose.ui.Alignment
import androidx.compose.ui.Modifier
import androidx.compose.ui.draw.clip
import androidx.compose.ui.graphics.Color
import androidx.compose.ui.text.font.FontWeight
import androidx.compose.ui.unit.dp
import androidx.compose.ui.unit.sp
import androidx.compose.foundation.lazy.rememberLazyListState
import com.mgacreative.hotel.shared.ui.designsystem.components.verticalScrollbar
import com.mgacreative.hotel.domain.model.Reservation
import com.mgacreative.hotel.domain.model.Room
import com.mgacreative.hotel.shared.ui.designsystem.components.VisualDatePicker
import com.mgacreative.hotel.shared.ui.designsystem.components.toDisplayDate
import kotlinx.datetime.Clock
import kotlinx.datetime.DayOfWeek
import kotlinx.datetime.LocalDate
import kotlinx.datetime.TimeZone
import kotlinx.datetime.toLocalDateTime
import org.koin.compose.viewmodel.koinViewModel

@Composable
fun NewReservationCalendarScreen(
    viewModel: NewReservationCalendarViewModel = koinViewModel(),
    onNavigate: (String) -> Unit = {}
) {
    val state by viewModel.state.collectAsState()
    val listState = rememberLazyListState()
    
    var reservationToDelete by remember { mutableStateOf<Reservation?>(null) }
    var reservationToEdit by remember { mutableStateOf<Reservation?>(null) }

    Scaffold(
        bottomBar = { CalendarBottomNavBar(currentRoute = "NewReservationCalendarScreen", onNavigate = onNavigate) }
    ) { paddingValues ->
        Column(
            modifier = Modifier
                .fillMaxSize()
                .padding(paddingValues)
                .background(Color(0xFFF8FAFC))
        ) {
            if (state.isLoading) {
                Box(modifier = Modifier.fillMaxSize(), contentAlignment = Alignment.Center) {
                    CircularProgressIndicator(color = Color(0xFF2563EB))
                }
            } else {
                LazyColumn(
                    state = listState,
                    modifier = Modifier.fillMaxSize().verticalScrollbar(listState),
                    contentPadding = PaddingValues(16.dp),
                    verticalArrangement = Arrangement.spacedBy(16.dp)
                ) {
                    // --- DATE NAVIGATION & KPI HEADER ---
                    item {
                        CalendarHeaderSection(
                            state = state,
                            onPrevClick = { viewModel.shiftDays(-14) },
                            onTodayClick = { viewModel.resetToToday() },
                            onNextClick = { viewModel.shiftDays(14) }
                        )
                    }

                    // --- MATRIX SECTION ---
                    item {
                        MatrixSection(state = state)
                    }

                    // --- SEARCH & ACTIONS HEADER ---
                    item {
                        Row(
                            modifier = Modifier.fillMaxWidth(),
                            verticalAlignment = Alignment.CenterVertically,
                            horizontalArrangement = Arrangement.SpaceBetween
                        ) {
                            Column {
                                Text(
                                    text = "Rezervasyon Listesi",
                                    fontWeight = FontWeight.Bold,
                                    fontSize = 16.sp,
                                    color = Color(0xFF0F172A)
                                )
                                Text(
                                    text = "${state.filteredReservations.size} kayıt listeleniyor",
                                    fontSize = 12.sp,
                                    color = Color(0xFF64748B)
                                )
                            }
                            
                            Button(
                                onClick = { onNavigate("CreateReservationScreen") },
                                colors = ButtonDefaults.buttonColors(containerColor = Color(0xFF2563EB)),
                                shape = RoundedCornerShape(10.dp),
                                contentPadding = PaddingValues(horizontal = 14.dp, vertical = 8.dp)
                            ) {
                                Icon(Icons.Default.Add, contentDescription = "Yeni", modifier = Modifier.size(16.dp))
                                Spacer(modifier = Modifier.width(6.dp))
                                Text("Yeni Rezervasyon", fontSize = 13.sp, fontWeight = FontWeight.SemiBold)
                            }
                        }
                    }

                    // --- SEARCH INPUT ---
                    item {
                        OutlinedTextField(
                            value = state.searchQuery,
                            onValueChange = { viewModel.updateSearchQuery(it) },
                            modifier = Modifier.fillMaxWidth(),
                            placeholder = { Text("Misafir Adı, Oda Tipi veya Tarih Ara...", fontSize = 13.sp) },
                            leadingIcon = { Icon(Icons.Default.Search, contentDescription = "Ara", tint = Color(0xFF64748B)) },
                            trailingIcon = {
                                if (state.searchQuery.isNotEmpty()) {
                                    IconButton(onClick = { viewModel.updateSearchQuery("") }) {
                                        Icon(Icons.Default.Close, contentDescription = "Temizle", tint = Color(0xFF94A3B8))
                                    }
                                }
                            },
                            singleLine = true,
                            shape = RoundedCornerShape(12.dp),
                            colors = OutlinedTextFieldDefaults.colors(
                                focusedBorderColor = Color(0xFF2563EB),
                                unfocusedBorderColor = Color(0xFFE2E8F0),
                                focusedContainerColor = Color.White,
                                unfocusedContainerColor = Color.White
                            )
                        )
                    }

                    // --- RESERVATION LIST ROWS ---
                    if (state.filteredReservations.isEmpty()) {
                        item {
                            Box(
                                modifier = Modifier
                                    .fillMaxWidth()
                                    .padding(vertical = 24.dp)
                                    .background(Color.White, RoundedCornerShape(12.dp))
                                    .border(1.dp, Color(0xFFE2E8F0), RoundedCornerShape(12.dp))
                                    .padding(24.dp),
                                contentAlignment = Alignment.Center
                            ) {
                                Column(horizontalAlignment = Alignment.CenterHorizontally) {
                                    Icon(Icons.Default.EventBusy, contentDescription = null, tint = Color(0xFF94A3B8), modifier = Modifier.size(40.dp))
                                    Spacer(modifier = Modifier.height(8.dp))
                                    Text("Kayıtlı rezervasyon bulunamadı.", fontSize = 14.sp, color = Color(0xFF64748B))
                                }
                            }
                        }
                    } else {
                        items(state.filteredReservations, key = { it.id ?: it.hashCode().toString() }) { res ->
                            ModernReservationCard(
                                reservation = res,
                                onEditClick = { reservationToEdit = res },
                                onDeleteClick = { reservationToDelete = res }
                            )
                        }
                    }
                    
                    item {
                        Spacer(modifier = Modifier.height(24.dp))
                    }
                }
            }
        }
    }

    // Edit Dialog
    if (reservationToEdit != null) {
        EditReservationDialog(
            reservation = reservationToEdit!!,
            onDismiss = { reservationToEdit = null },
            onSave = { updatedReservation ->
                viewModel.updateReservation(updatedReservation)
                reservationToEdit = null
            }
        )
    }

    // Delete Confirmation Dialog
    if (reservationToDelete != null) {
        AlertDialog(
            onDismissRequest = { reservationToDelete = null },
            containerColor = Color.White,
            shape = RoundedCornerShape(16.dp),
            title = { Text("Rezervasyonu Sil", fontWeight = FontWeight.Bold, color = Color(0xFF0F172A)) },
            text = { Text("Bu rezervasyonu silmek istediğinize emin misiniz? Bu işlem geri alınamaz.", color = Color(0xFF475569)) },
            confirmButton = {
                Button(
                    onClick = {
                        reservationToDelete?.id?.let { viewModel.deleteReservation(it) }
                        reservationToDelete = null
                    },
                    colors = ButtonDefaults.buttonColors(containerColor = Color(0xFFDC2626)),
                    shape = RoundedCornerShape(8.dp)
                ) {
                    Text("Sil", fontWeight = FontWeight.Bold)
                }
            },
            dismissButton = {
                OutlinedButton(
                    onClick = { reservationToDelete = null },
                    shape = RoundedCornerShape(8.dp)
                ) {
                    Text("İptal", color = Color(0xFF475569))
                }
            }
        )
    }
}

@Composable
fun CalendarHeaderSection(
    state: NewCalendarState,
    onPrevClick: () -> Unit,
    onTodayClick: () -> Unit,
    onNextClick: () -> Unit
) {
    val firstDate = state.dates.firstOrNull()
    val lastDate = state.dates.lastOrNull()
    val dateRangeText = if (firstDate != null && lastDate != null) {
        "${firstDate.dayOfMonth} ${getMonthName(firstDate.monthNumber)} - ${lastDate.dayOfMonth} ${getMonthName(lastDate.monthNumber)} ${lastDate.year}"
    } else {
        "Oda Müsaitlik Matrisi"
    }

    Surface(
        shape = RoundedCornerShape(12.dp),
        color = Color.White,
        border = androidx.compose.foundation.BorderStroke(1.dp, Color(0xFFE2E8F0)),
        modifier = Modifier.fillMaxWidth()
    ) {
        Column(modifier = Modifier.padding(14.dp)) {
            Row(
                modifier = Modifier.fillMaxWidth(),
                verticalAlignment = Alignment.CenterVertically,
                horizontalArrangement = Arrangement.SpaceBetween
            ) {
                // Title and Date Range
                Column {
                    Text(
                        text = "Oda Tiplerine Göre Müsaitlik Matrisi",
                        fontWeight = FontWeight.Bold,
                        fontSize = 15.sp,
                        color = Color(0xFF0F172A)
                    )
                    Spacer(modifier = Modifier.height(2.dp))
                    Text(
                        text = dateRangeText,
                        fontSize = 13.sp,
                        fontWeight = FontWeight.SemiBold,
                        color = Color(0xFF2563EB)
                    )
                }

                // Date Navigation Controls
                Row(
                    verticalAlignment = Alignment.CenterVertically,
                    horizontalArrangement = Arrangement.spacedBy(6.dp)
                ) {
                    OutlinedButton(
                        onClick = onPrevClick,
                        shape = RoundedCornerShape(8.dp),
                        contentPadding = PaddingValues(horizontal = 8.dp, vertical = 4.dp),
                        modifier = Modifier.height(34.dp)
                    ) {
                        Icon(Icons.Default.ChevronLeft, contentDescription = "Önceki", modifier = Modifier.size(16.dp))
                        Text("14 Gün", fontSize = 11.sp, fontWeight = FontWeight.Medium)
                    }

                    Button(
                        onClick = onTodayClick,
                        colors = ButtonDefaults.buttonColors(containerColor = Color(0xFF0F172A)),
                        shape = RoundedCornerShape(8.dp),
                        contentPadding = PaddingValues(horizontal = 10.dp, vertical = 4.dp),
                        modifier = Modifier.height(34.dp)
                    ) {
                        Text("Bugün", fontSize = 11.sp, fontWeight = FontWeight.Bold)
                    }

                    OutlinedButton(
                        onClick = onNextClick,
                        shape = RoundedCornerShape(8.dp),
                        contentPadding = PaddingValues(horizontal = 8.dp, vertical = 4.dp),
                        modifier = Modifier.height(34.dp)
                    ) {
                        Text("14 Gün", fontSize = 11.sp, fontWeight = FontWeight.Medium)
                        Icon(Icons.Default.ChevronRight, contentDescription = "Sonraki", modifier = Modifier.size(16.dp))
                    }
                }
            }

            Spacer(modifier = Modifier.height(10.dp))
            HorizontalDivider(color = Color(0xFFF1F5F9))
            Spacer(modifier = Modifier.height(8.dp))

            // Legend indicators
            Row(
                modifier = Modifier.fillMaxWidth(),
                verticalAlignment = Alignment.CenterVertically,
                horizontalArrangement = Arrangement.spacedBy(16.dp)
            ) {
                LegendChip(color = Color(0xFF16A34A), bgColor = Color(0xFFDCFCE7), label = "Müsait (>2)")
                LegendChip(color = Color(0xFFD97706), bgColor = Color(0xFFFEF3C7), label = "Kritik / Az Kaldı (≤2)")
                LegendChip(color = Color(0xFFDC2626), bgColor = Color(0xFFFEE2E2), label = "DOLU (0)")
            }
        }
    }
}

@Composable
fun LegendChip(color: Color, bgColor: Color, label: String) {
    Row(
        verticalAlignment = Alignment.CenterVertically,
        horizontalArrangement = Arrangement.spacedBy(5.dp)
    ) {
        Box(
            modifier = Modifier
                .size(10.dp)
                .clip(CircleShape)
                .background(color)
        )
        Text(text = label, fontSize = 11.sp, color = Color(0xFF475569), fontWeight = FontWeight.Medium)
    }
}

@Composable
fun MatrixSection(state: NewCalendarState) {
    val scrollState = rememberScrollState()
    val headerWidth = 130.dp
    val cellWidth = 72.dp
    val headerHeight = 50.dp
    val rowHeight = 52.dp
    
    val today = remember { Clock.System.now().toLocalDateTime(TimeZone.currentSystemDefault()).date }
    val roomTypes = remember(state.rooms) { state.rooms.map { it.roomType }.distinct().sorted() }

    Surface(
        shape = RoundedCornerShape(12.dp),
        color = Color.White,
        border = androidx.compose.foundation.BorderStroke(1.dp, Color(0xFFE2E8F0)),
        modifier = Modifier.fillMaxWidth()
    ) {
        Column(
            modifier = Modifier
                .fillMaxWidth()
                .horizontalScroll(scrollState)
        ) {
            // --- TOP HEADER ROW (DATES & WEEKDAYS) ---
            Row(
                modifier = Modifier
                    .height(headerHeight)
                    .background(Color(0xFFF8FAFC))
            ) {
                // Room Type Header Box
                Box(
                    modifier = Modifier
                        .width(headerWidth)
                        .fillMaxHeight()
                        .border(0.5.dp, Color(0xFFE2E8F0))
                        .padding(horizontal = 10.dp),
                    contentAlignment = Alignment.CenterStart
                ) {
                    Row(verticalAlignment = Alignment.CenterVertically) {
                        Icon(Icons.Default.MeetingRoom, contentDescription = null, tint = Color(0xFF64748B), modifier = Modifier.size(14.dp))
                        Spacer(modifier = Modifier.width(4.dp))
                        Text("Oda Tipi", fontWeight = FontWeight.Bold, fontSize = 12.sp, color = Color(0xFF334155))
                    }
                }
                
                // Date Columns
                state.dates.forEach { date ->
                    val isToday = date == today
                    val isWeekend = date.dayOfWeek == DayOfWeek.SATURDAY || date.dayOfWeek == DayOfWeek.SUNDAY
                    
                    val headerBg = when {
                        isToday -> Color(0xFFEFF6FF)
                        isWeekend -> Color(0xFFFFF7ED)
                        else -> Color(0xFFF8FAFC)
                    }

                    Box(
                        modifier = Modifier
                            .width(cellWidth)
                            .fillMaxHeight()
                            .background(headerBg)
                            .border(0.5.dp, if (isToday) Color(0xFF93C5FD) else Color(0xFFE2E8F0))
                            .padding(vertical = 4.dp),
                        contentAlignment = Alignment.Center
                    ) {
                        Column(horizontalAlignment = Alignment.CenterHorizontally) {
                            Text(
                                text = date.dayOfWeek.toTurkishShort(),
                                fontSize = 10.sp,
                                fontWeight = if (isToday || isWeekend) FontWeight.Bold else FontWeight.Medium,
                                color = when {
                                    isToday -> Color(0xFF2563EB)
                                    isWeekend -> Color(0xFFEA580C)
                                    else -> Color(0xFF64748B)
                                }
                            )
                            Spacer(modifier = Modifier.height(2.dp))
                            Text(
                                text = "${date.dayOfMonth}/${date.monthNumber}",
                                fontSize = 12.sp,
                                fontWeight = if (isToday) FontWeight.Bold else FontWeight.SemiBold,
                                color = if (isToday) Color(0xFF1D4ED8) else Color(0xFF1E293B)
                            )
                            if (isToday) {
                                Box(
                                    modifier = Modifier
                                        .padding(top = 2.dp)
                                        .background(Color(0xFF2563EB), RoundedCornerShape(4.dp))
                                        .padding(horizontal = 4.dp, vertical = 1.dp)
                                ) {
                                    Text("Bugün", fontSize = 8.sp, color = Color.White, fontWeight = FontWeight.Bold)
                                }
                            }
                        }
                    }
                }
            }
            
            // --- DATA ROWS (ROOM TYPES) ---
            roomTypes.forEach { roomType ->
                val totalRoomsOfType = state.rooms.count { it.roomType == roomType }
                
                Row(modifier = Modifier.height(rowHeight)) {
                    // Room Type Label
                    Box(
                        modifier = Modifier
                            .width(headerWidth)
                            .fillMaxHeight()
                            .background(Color.White)
                            .border(0.5.dp, Color(0xFFE2E8F0))
                            .padding(horizontal = 10.dp),
                        contentAlignment = Alignment.CenterStart
                    ) {
                        Column {
                            Text(
                                text = roomType,
                                fontSize = 12.sp,
                                color = Color(0xFF1E293B),
                                fontWeight = FontWeight.SemiBold,
                                maxLines = 1
                            )
                            Text(
                                text = "$totalRoomsOfType Oda",
                                fontSize = 10.sp,
                                color = Color(0xFF64748B)
                            )
                        }
                    }
                    
                    // Cells for each date
                    state.dates.forEach { date ->
                        val reservationsForType = state.reservations.filter { it.roomType == roomType || it.rooms?.roomType == roomType }
                        val reservedCount = reservationsForType.count { res ->
                            val checkIn = try { LocalDate.parse(res.checkInDate) } catch (e: Exception) { null }
                            val checkOut = try { LocalDate.parse(res.checkOutDate) } catch (e: Exception) { null }
                            checkIn != null && checkOut != null && date >= checkIn && date < checkOut
                        }
                        val availableCount = (totalRoomsOfType - reservedCount).coerceAtLeast(0)
                        
                        val isSoldOut = availableCount <= 0 && totalRoomsOfType > 0
                        val isLow = availableCount in 1..2 && totalRoomsOfType > 2
                        
                        val cellBg = when {
                            isSoldOut -> Color(0xFFFEE2E2)
                            isLow -> Color(0xFFFEF3C7)
                            else -> Color(0xFFF0FDF4)
                        }
                        
                        val cellBorder = when {
                            isSoldOut -> Color(0xFFFECACA)
                            isLow -> Color(0xFFFDE68A)
                            else -> Color(0xFFDCFCE7)
                        }

                        val numberColor = when {
                            isSoldOut -> Color(0xFFDC2626)
                            isLow -> Color(0xFFD97706)
                            else -> Color(0xFF16A34A)
                        }

                        Box(
                            modifier = Modifier
                                .width(cellWidth)
                                .fillMaxHeight()
                                .background(cellBg)
                                .border(0.5.dp, cellBorder)
                                .padding(horizontal = 4.dp, vertical = 4.dp),
                            contentAlignment = Alignment.Center
                        ) {
                            Column(
                                horizontalAlignment = Alignment.CenterHorizontally,
                                verticalArrangement = Arrangement.Center
                            ) {
                                Text(
                                    text = "$availableCount",
                                    fontSize = 14.sp,
                                    color = numberColor,
                                    fontWeight = FontWeight.Bold
                                )
                                Text(
                                    text = if (isSoldOut) "DOLU" else "Dolu: $reservedCount",
                                    fontSize = 9.sp,
                                    color = if (isSoldOut) Color(0xFFDC2626) else Color(0xFF64748B),
                                    fontWeight = if (isSoldOut) FontWeight.Bold else FontWeight.Normal
                                )
                            }
                        }
                    }
                }
            }

            // --- SUMMARY ROW: TOPLAM MÜSAİTLİK ---
            Row(
                modifier = Modifier
                    .height(44.dp)
                    .background(Color(0xFFF8FAFC))
            ) {
                Box(
                    modifier = Modifier
                        .width(headerWidth)
                        .fillMaxHeight()
                        .border(0.5.dp, Color(0xFFCBD5E1))
                        .padding(horizontal = 10.dp),
                    contentAlignment = Alignment.CenterStart
                ) {
                    Text(
                        text = "Toplam Müsait",
                        fontWeight = FontWeight.Bold,
                        fontSize = 11.sp,
                        color = Color(0xFF334155)
                    )
                }

                state.dates.forEach { date ->
                    val totalHotelRooms = state.rooms.size
                    val totalReservedToday = state.reservations.count { res ->
                        val checkIn = try { LocalDate.parse(res.checkInDate) } catch (e: Exception) { null }
                        val checkOut = try { LocalDate.parse(res.checkOutDate) } catch (e: Exception) { null }
                        checkIn != null && checkOut != null && date >= checkIn && date < checkOut
                    }
                    val totalAvailableToday = (totalHotelRooms - totalReservedToday).coerceAtLeast(0)

                    Box(
                        modifier = Modifier
                            .width(cellWidth)
                            .fillMaxHeight()
                            .border(0.5.dp, Color(0xFFCBD5E1)),
                        contentAlignment = Alignment.Center
                    ) {
                        Text(
                            text = "$totalAvailableToday / $totalHotelRooms",
                            fontSize = 11.sp,
                            fontWeight = FontWeight.Bold,
                            color = Color(0xFF0F172A)
                        )
                    }
                }
            }

            // --- SUMMARY ROW: DOLULUK ORANI (%) ---
            Row(
                modifier = Modifier
                    .height(40.dp)
                    .background(Color(0xFFF1F5F9))
            ) {
                Box(
                    modifier = Modifier
                        .width(headerWidth)
                        .fillMaxHeight()
                        .border(0.5.dp, Color(0xFFCBD5E1))
                        .padding(horizontal = 10.dp),
                    contentAlignment = Alignment.CenterStart
                ) {
                    Text(
                        text = "Doluluk %",
                        fontWeight = FontWeight.Bold,
                        fontSize = 11.sp,
                        color = Color(0xFF334155)
                    )
                }

                state.dates.forEach { date ->
                    val totalHotelRooms = state.rooms.size
                    val totalReservedToday = state.reservations.count { res ->
                        val checkIn = try { LocalDate.parse(res.checkInDate) } catch (e: Exception) { null }
                        val checkOut = try { LocalDate.parse(res.checkOutDate) } catch (e: Exception) { null }
                        checkIn != null && checkOut != null && date >= checkIn && date < checkOut
                    }
                    val occupancyPercent = if (totalHotelRooms > 0) {
                        ((totalReservedToday.toDouble() / totalHotelRooms) * 100).toInt()
                    } else 0

                    val occColor = when {
                        occupancyPercent >= 85 -> Color(0xFFDC2626)
                        occupancyPercent >= 50 -> Color(0xFFD97706)
                        else -> Color(0xFF16A34A)
                    }

                    Box(
                        modifier = Modifier
                            .width(cellWidth)
                            .fillMaxHeight()
                            .border(0.5.dp, Color(0xFFCBD5E1)),
                        contentAlignment = Alignment.Center
                    ) {
                        Text(
                            text = "%$occupancyPercent",
                            fontSize = 11.sp,
                            fontWeight = FontWeight.Bold,
                            color = occColor
                        )
                    }
                }
            }
        }
    }
}

@Composable
fun ReservationListRow(
    reservation: Reservation,
    onEditClick: () -> Unit,
    onDeleteClick: () -> Unit
) {
    ModernReservationCard(
        reservation = reservation,
        onEditClick = onEditClick,
        onDeleteClick = onDeleteClick
    )
}

@Composable
fun ModernReservationCard(
    reservation: Reservation,
    onEditClick: () -> Unit,
    onDeleteClick: () -> Unit
) {
    val guestName = "${reservation.guests?.firstName ?: ""} ${reservation.guests?.lastName ?: ""}".trim().takeIf { it.isNotBlank() } ?: "İsimsiz Misafir"
    val initials = guestName.split(" ").mapNotNull { it.firstOrNull()?.toString() }.take(2).joinToString("").uppercase()
    val roomTypeStr = reservation.roomType ?: reservation.rooms?.roomType ?: "Belirtilmemiş"

    Surface(
        shape = RoundedCornerShape(12.dp),
        color = Color.White,
        border = androidx.compose.foundation.BorderStroke(1.dp, Color(0xFFE2E8F0)),
        modifier = Modifier.fillMaxWidth()
    ) {
        Row(
            modifier = Modifier
                .fillMaxWidth()
                .padding(14.dp),
            verticalAlignment = Alignment.CenterVertically,
            horizontalArrangement = Arrangement.SpaceBetween
        ) {
            // Guest & Booking Details
            Row(
                verticalAlignment = Alignment.CenterVertically,
                modifier = Modifier.weight(1f),
                horizontalArrangement = Arrangement.spacedBy(12.dp)
            ) {
                // Avatar badge
                Box(
                    modifier = Modifier
                        .size(40.dp)
                        .clip(CircleShape)
                        .background(Color(0xFFEFF6FF)),
                    contentAlignment = Alignment.Center
                ) {
                    Text(
                        text = if (initials.isNotBlank()) initials else "M",
                        fontWeight = FontWeight.Bold,
                        fontSize = 14.sp,
                        color = Color(0xFF2563EB)
                    )
                }

                Column(verticalArrangement = Arrangement.spacedBy(4.dp)) {
                    Text(
                        text = guestName,
                        fontWeight = FontWeight.Bold,
                        fontSize = 14.sp,
                        color = Color(0xFF0F172A)
                    )

                    Row(
                        verticalAlignment = Alignment.CenterVertically,
                        horizontalArrangement = Arrangement.spacedBy(8.dp)
                    ) {
                        Row(verticalAlignment = Alignment.CenterVertically) {
                            Icon(Icons.Default.DateRange, contentDescription = null, tint = Color(0xFF64748B), modifier = Modifier.size(13.dp))
                            Spacer(modifier = Modifier.width(3.dp))
                            Text(
                                text = "${reservation.checkInDate} → ${reservation.checkOutDate}",
                                fontSize = 12.sp,
                                color = Color(0xFF475569),
                                fontWeight = FontWeight.Medium
                            )
                        }

                        // Room Type Pill
                        Box(
                            modifier = Modifier
                                .background(Color(0xFFF1F5F9), RoundedCornerShape(6.dp))
                                .padding(horizontal = 6.dp, vertical = 2.dp)
                        ) {
                            Text(
                                text = roomTypeStr,
                                fontSize = 11.sp,
                                fontWeight = FontWeight.SemiBold,
                                color = Color(0xFF334155)
                            )
                        }

                        // Occupancy (Guests)
                        Text(
                            text = "${reservation.adultsCount} Yetişkin, ${reservation.childrenCount} Çocuk",
                            fontSize = 11.sp,
                            color = Color(0xFF64748B)
                        )
                    }
                }
            }

            // Price and Actions
            Row(
                verticalAlignment = Alignment.CenterVertically,
                horizontalArrangement = Arrangement.spacedBy(8.dp)
            ) {
                if (reservation.totalPrice > 0.0) {
                    Text(
                        text = "₺ ${reservation.totalPrice.toInt()}",
                        fontWeight = FontWeight.Bold,
                        fontSize = 14.sp,
                        color = Color(0xFF059669)
                    )
                }

                IconButton(
                    onClick = onEditClick,
                    modifier = Modifier
                        .size(32.dp)
                        .clip(RoundedCornerShape(8.dp))
                        .background(Color(0xFFEFF6FF))
                ) {
                    Icon(Icons.Default.Edit, contentDescription = "Düzenle", tint = Color(0xFF2563EB), modifier = Modifier.size(16.dp))
                }

                IconButton(
                    onClick = onDeleteClick,
                    modifier = Modifier
                        .size(32.dp)
                        .clip(RoundedCornerShape(8.dp))
                        .background(Color(0xFFFEE2E2))
                ) {
                    Icon(Icons.Default.Delete, contentDescription = "Sil", tint = Color(0xFFDC2626), modifier = Modifier.size(16.dp))
                }
            }
        }
    }
}

@Composable
fun EditReservationDialog(
    reservation: Reservation,
    onDismiss: () -> Unit,
    onSave: (Reservation) -> Unit
) {
    var checkIn by remember { mutableStateOf(reservation.checkInDate) }
    var checkOut by remember { mutableStateOf(reservation.checkOutDate) }
    var roomType by remember { mutableStateOf(reservation.roomType ?: "") }
    var note by remember { mutableStateOf(reservation.specialNotes ?: "") }
    var adults by remember { mutableStateOf(reservation.adultsCount.toString()) }
    var children by remember { mutableStateOf(reservation.childrenCount.toString()) }
    var price by remember { mutableStateOf(reservation.totalPrice.toString()) }
    var roomId by remember { mutableStateOf(reservation.roomId ?: "") }
    var showCheckInPicker by remember { mutableStateOf(false) }
    var showCheckOutPicker by remember { mutableStateOf(false) }
    
    val checkInDateParsed = try { LocalDate.parse(checkIn) } catch (e: Exception) { Clock.System.now().toLocalDateTime(TimeZone.currentSystemDefault()).date }
    val checkOutDateParsed = try { LocalDate.parse(checkOut) } catch (e: Exception) { Clock.System.now().toLocalDateTime(TimeZone.currentSystemDefault()).date }

    if (showCheckInPicker) {
        VisualDatePicker(
            initialDate = checkInDateParsed,
            onDateSelected = { 
                checkIn = it.toString()
                showCheckInPicker = false 
            },
            onDismissRequest = { showCheckInPicker = false }
        )
    }

    if (showCheckOutPicker) {
        VisualDatePicker(
            initialDate = checkOutDateParsed,
            onDateSelected = { 
                checkOut = it.toString()
                showCheckOutPicker = false 
            },
            onDismissRequest = { showCheckOutPicker = false }
        )
    }

    AlertDialog(
        onDismissRequest = onDismiss,
        containerColor = Color.White,
        shape = RoundedCornerShape(16.dp),
        title = { Text("Rezervasyon Düzenle", fontWeight = FontWeight.Bold, color = Color(0xFF0F172A)) },
        text = {
            Column(
                verticalArrangement = Arrangement.spacedBy(12.dp),
                modifier = Modifier.verticalScroll(rememberScrollState())
            ) {
                val guestName = "${reservation.guests?.firstName ?: ""} ${reservation.guests?.lastName ?: ""}".trim()
                Text("Misafir: $guestName", fontWeight = FontWeight.SemiBold, color = Color(0xFF334155))
                
                Box(modifier = Modifier.fillMaxWidth().clickable { showCheckInPicker = true }) {
                    OutlinedTextField(
                        value = if (checkIn.isNotBlank()) checkIn.toDisplayDate() else "",
                        onValueChange = { },
                        label = { Text("Giriş Tarihi") },
                        singleLine = true,
                        modifier = Modifier.fillMaxWidth(),
                        enabled = false,
                        shape = RoundedCornerShape(10.dp),
                        colors = OutlinedTextFieldDefaults.colors(
                            disabledTextColor = Color(0xFF0F172A),
                            disabledBorderColor = Color(0xFFCBD5E1),
                            disabledLabelColor = Color(0xFF64748B)
                        )
                    )
                }
                
                Box(modifier = Modifier.fillMaxWidth().clickable { showCheckOutPicker = true }) {
                    OutlinedTextField(
                        value = if (checkOut.isNotBlank()) checkOut.toDisplayDate() else "",
                        onValueChange = { },
                        label = { Text("Çıkış Tarihi") },
                        singleLine = true,
                        modifier = Modifier.fillMaxWidth(),
                        enabled = false,
                        shape = RoundedCornerShape(10.dp),
                        colors = OutlinedTextFieldDefaults.colors(
                            disabledTextColor = Color(0xFF0F172A),
                            disabledBorderColor = Color(0xFFCBD5E1),
                            disabledLabelColor = Color(0xFF64748B)
                        )
                    )
                }
                
                OutlinedTextField(
                    value = roomType,
                    onValueChange = { roomType = it },
                    label = { Text("Oda Tipi") },
                    singleLine = true,
                    shape = RoundedCornerShape(10.dp),
                    modifier = Modifier.fillMaxWidth()
                )
                
                OutlinedTextField(
                    value = roomId,
                    onValueChange = { roomId = it },
                    label = { Text("Oda Numarası (ID)") },
                    singleLine = true,
                    shape = RoundedCornerShape(10.dp),
                    modifier = Modifier.fillMaxWidth()
                )

                Row(horizontalArrangement = Arrangement.spacedBy(8.dp)) {
                    OutlinedTextField(
                        value = adults,
                        onValueChange = { adults = it },
                        label = { Text("Yetişkin") },
                        singleLine = true,
                        shape = RoundedCornerShape(10.dp),
                        modifier = Modifier.weight(1f)
                    )
                    OutlinedTextField(
                        value = children,
                        onValueChange = { children = it },
                        label = { Text("Çocuk") },
                        singleLine = true,
                        shape = RoundedCornerShape(10.dp),
                        modifier = Modifier.weight(1f)
                    )
                }

                OutlinedTextField(
                    value = price,
                    onValueChange = { price = it },
                    label = { Text("Toplam Fiyat (₺)") },
                    singleLine = true,
                    shape = RoundedCornerShape(10.dp),
                    modifier = Modifier.fillMaxWidth()
                )
                
                OutlinedTextField(
                    value = note,
                    onValueChange = { note = it },
                    label = { Text("Özel Notlar") },
                    shape = RoundedCornerShape(10.dp),
                    modifier = Modifier.fillMaxWidth(),
                    maxLines = 3
                )
            }
        },
        confirmButton = {
            Button(
                onClick = {
                    val updated = reservation.copy(
                        checkInDate = checkIn,
                        checkOutDate = checkOut,
                        roomType = roomType.takeIf { it.isNotBlank() },
                        specialNotes = note.takeIf { it.isNotBlank() },
                        adultsCount = adults.toIntOrNull() ?: reservation.adultsCount,
                        childrenCount = children.toIntOrNull() ?: reservation.childrenCount,
                        totalPrice = price.toDoubleOrNull() ?: reservation.totalPrice,
                        roomId = roomId.takeIf { it.isNotBlank() }
                    )
                    onSave(updated)
                },
                colors = ButtonDefaults.buttonColors(containerColor = Color(0xFF2563EB)),
                shape = RoundedCornerShape(8.dp)
            ) {
                Text("Kaydet", fontWeight = FontWeight.Bold)
            }
        },
        dismissButton = {
            OutlinedButton(
                onClick = onDismiss,
                shape = RoundedCornerShape(8.dp)
            ) {
                Text("İptal", color = Color(0xFF475569))
            }
        }
    )
}

private fun DayOfWeek.toTurkishShort(): String = when (this) {
    DayOfWeek.MONDAY -> "Pzt"
    DayOfWeek.TUESDAY -> "Sal"
    DayOfWeek.WEDNESDAY -> "Çar"
    DayOfWeek.THURSDAY -> "Per"
    DayOfWeek.FRIDAY -> "Cum"
    DayOfWeek.SATURDAY -> "Cmt"
    DayOfWeek.SUNDAY -> "Paz"
}

private fun getMonthName(month: Int): String = when (month) {
    1 -> "Oca"
    2 -> "Şub"
    3 -> "Mar"
    4 -> "Nis"
    5 -> "May"
    6 -> "Haz"
    7 -> "Tem"
    8 -> "Ağu"
    9 -> "Eyl"
    10 -> "Eki"
    11 -> "Kas"
    12 -> "Ara"
    else -> ""
}
